import { Inline } from '@bedrock-layout/primitives';

/* eslint-disable */
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { ChangeEvent, Ref, forwardRef, useEffect, useState } from 'react';
import {
  Control,
  UseFormSetValue,
  useController,
  useWatch,
} from 'react-hook-form';
import {
  Dataset,
  ExecutedValueTooltip,
  TextInputInModal,
  Typography,
  formatNectedDate,
  useLayer,
} from 'ui';

import {
  convertCaSampleValues,
  extractSourceAndAttributeFromValue,
  getPropertyIfExists,
  isFieldReadOnly,
} from '../../../../utils/common';
import { LIST_KEY_REGEX } from '../../../../utils/regex';
import type { sendEventToGTMType } from '../../types';
import { getOutputPlaceholder } from '../../utils/common';
import { ErrorPopoverPositioned } from '../SimpleRule/Error/ErrorPopoverPositioned';
import type { ResultConditionType } from './ResultRhs';
import {
  ExpandIconContainer,
  InputBrackets,
  InputContainer,
  InputStyled,
  InputWithPillStyled,
  RightIconContainer,
  TextAreaStyled,
} from './ResultRhs.styled';

type RhsLauncherProps = {
  nodeName: string;
  from: string;
  panelVisible: boolean;
  text: string | number;
  dataType: string;
  control?: Control<any>;
  setValue?: UseFormSetValue<any>;
  handleSendEventToGTM?: (obj: sendEventToGTMType) => void;
  nodeType?: string;
  isSmall?: boolean;
  isFull?: boolean;
  type: ResultConditionType;
  disabled: boolean;
  onlyJson?: boolean;
  showTooltip?: boolean;
  dataset?: Record<string, Dataset>;
  showExpandIconIfList?: boolean;
  sourceKey?: string;
  attributeKey?: string;
  nodeTypeName?: string;
  rightIcon?: any;
  forceExpandListIcon?: boolean;
  readOnly?: boolean;
  inputType?: 'input' | 'textarea';
  isRelativeDate?: boolean;
  placeholder?: string;
  hidePopover?: () => void;
};

export const RhsLauncher = forwardRef(
  (
    {
      hidePopover,
      from,
      text,
      nodeName,
      panelVisible = false,
      dataType,
      handleSendEventToGTM,
      control,
      setValue,
      nodeType,
      isSmall = false,
      isFull = false,
      type = '',
      disabled,
      onlyJson = false,
      showTooltip = false,
      dataset = {},
      showExpandIconIfList = true,
      sourceKey = 'source',
      attributeKey = 'attribute',
      nodeTypeName,
      rightIcon,
      forceExpandListIcon = false,
      readOnly = false,
      inputType = 'input',
      isRelativeDate = false,
      placeholder = '',
    }: RhsLauncherProps,
    ref: Ref<any>
  ) => {
    const [nType, setNodeType] = useState(nodeType);
    const [value, setLocalValue] = useState<any>(text);
    const [showTooltipLocal, setShowTooltipLocal] = useState(
      showTooltip && nodeType !== 'constant'
    );
    const [isFocused, setIsFocused] = useState(false);

    const { openWithProps: openTextInputModal } = useLayer(
      <TextInputInModal value={value} />
    );

    const { fieldState } = useController({
      name: `${nodeName}.value`,
      control,
    });

    const nodeVal = useWatch({
      control,
      name: nodeName,
    });

    function checkRuleType() {
      const urlParams = new URLSearchParams(window.location.search);
      const ruleType = urlParams.get('ruleType');
      return ruleType;;
    }

    const ruleType = checkRuleType();

    let source: string | undefined, attribute: string | undefined;

    if (isRelativeDate) {
      const { source: sourceType, attribute: attributeType } =
        extractSourceAndAttributeFromValue(value, dataset);

      source = sourceType;
      attribute = attributeType;
    } else {
      source = nodeVal?.[sourceKey];
      attribute = nodeVal?.[attributeKey];
    }

    const tooltipMessage = (() => {
      if (_isNil(source) || _isEmpty(source) || _isNil(dataset[source])) {
        return attribute;
      }
      
      try {
        const attributes = dataset[source].attributes;
        if (!attributes) {
          return attribute;
        }
        
        const transformedData = Object.keys(attributes).reduce(
          (acc, curr) => {
            try {
              return {
                ...acc,
                [curr]: attributes[curr].executedValue,
              };
            } catch (e) {
              return acc;
            }
          },
          {}
        );
        
        return getPropertyIfExists(transformedData, attribute ?? '') ?? attribute;
      } catch (error) {
        return attribute;
      }
    })();


    const handleClick = () => {
      if (typeof handleSendEventToGTM === 'function') {
        handleSendEventToGTM({
          action: _isEmpty(text) ? 'add' : 'edit',
          element: 'rhs_value',
          actionName: dataType ?? '',
        });
      }
    };

    useEffect(() => {
      setLocalValue(text.toString());
    }, [text]);

    useEffect(() => {
      setNodeType(nodeType);
    }, [nodeType]);

    useEffect(() => {
      setShowTooltipLocal(showTooltip && nType !== 'constant');
    }, [nType]);

    const error = fieldState.error?.message;

    const onInputChange = (e: ChangeEvent<any>) => {
      e.preventDefault();
      const val = e.target.value.toString();
      // eslint-disable-next-line
      const isList = !!val.match(LIST_KEY_REGEX)?.length;
      setLocalValue(e.target.value);
      const localNodeType = isList ? 'token' : 'constant';

      setNodeType(localNodeType);

      setShowTooltipLocal(false);
    };

    const handleOnBlur = () => {
      setTimeout(() => {
        setIsFocused(false);
      }, 100);

      let valueNodeName = `${nodeName}.value`;

      if (isRelativeDate) {
        valueNodeName += '.value';
      }

      if (nType === 'constant' && !panelVisible) {
        if (typeof setValue === 'function') {
          setValue(valueNodeName, value);
          setValue(
            `${nodeName}.executedValue`,
            convertCaSampleValues(dataType, value)
          );
          setValue(`${nodeName}.source`, null);
          setValue(`${nodeName}.nodeType`, 'constant');
          setValue(`${nodeName}.sourceType`, null);
          setValue(`${nodeName}.attribute`, null);
        }
      } else if (nType !== 'constant') {
        if (type === 'workflowDT' || type === '') {
          if (LIST_KEY_REGEX.test(value.toString())) {
            const values = value.toString().split('.');

            if (typeof setValue === 'function') {
              setValue(valueNodeName, null);
              setValue(`${nodeName}.source`, values[0]);
              setValue(
                `${nodeName}.nodeType`,
                isRelativeDate ? 'noCodeFunc' : 'params'
              );
              setValue(
                `${nodeName}.attribute`,
                values.filter((x: any, i: number) => i !== 0).join('.')
              );
            }
          }
        }
      }

      setShowTooltipLocal(showTooltip && nType !== 'constant');
    };

    const showExpandIcon = showExpandIconIfList
      ? nType === 'constant' && dataType === 'list'
      : false;

    const formatValue = (input: any) => {
      if (dataType === 'date' && nType === 'constant') {
        try {
          return formatNectedDate(input, 'date');
        } catch (err) {
          return input;
        }
      }

      if (dataType === 'dateTime' && nType === 'constant') {
        try {
          return formatNectedDate(input, 'dateTime');
        } catch (err) {
          return input;
        }
      }

      if (
        isRelativeDate &&
        ['date', 'dateTime'].includes(dataType) &&
        nType === 'noCodeFunc'
      ) {
        return input.replace('{{.', '').replace('}}', '');
      }

      return input;
    };

    const isValueToken = !_isNil(source) && !_isEmpty(source);

    const fieldHasPillComponent = [
      'json',
      'list',
      'jsFormula',
      'excelFormula',
    ].includes(dataType);

    const formattedValue = formatValue(value);



    return (
      <InputContainer align="center" gutter={0} onClick={handleClick} ref={ref}>
        {!_isNil(error) && <ErrorPopoverPositioned error={error} />}

        {nType !== 'constant' && isValueToken && !fieldHasPillComponent && (
          <InputBrackets direction="left">
            <Typography>{'{{'}</Typography>
          </InputBrackets>
        )}

        <ExecutedValueTooltip
          attribute={attribute}
          value={tooltipMessage}
          dataType={dataType}
          id={nodeName}
          placement="top-end"
          isVisible={showTooltipLocal && isValueToken}
        >
          {fieldHasPillComponent ? (
            <InputWithPillStyled
              $hasPill={!_isNil(rightIcon) && fieldHasPillComponent}
              $hasError={!_isNil(error)}
              $isSmall={isSmall}
              $isFull={isFull}
              $isPlaceholder={
                _isNil(formattedValue) || _isEmpty(formattedValue)
              }
            >
              <Inline align="center" gutter="0.5rem">
                {nType !== 'constant' && isValueToken && (
                  <Typography>{'{{'}</Typography>
                )}
                <div className="text-style">
                  {_isNil(formattedValue) || _isEmpty(formattedValue)
                    ? 'Enter value'
                    : formatValue(value).length > 12 ? formatValue(value).substring(0, 12) + '...' : formatValue(value)}
                </div>
                {nType !== 'constant' && isValueToken && (
                  <Typography>{'}}'}</Typography>
                )}
              </Inline>
            </InputWithPillStyled>
          ) : inputType === 'input' ? (
            <InputStyled
              value={formatValue(value)}
              $isSmall={isSmall}
              $hasError={!_isNil(error)}
              $showExpandIcon={showExpandIcon}
              onChange={onInputChange}
              onBlur={handleOnBlur}
              onFocus={() => setIsFocused(true)}
              placeholder={placeholder ? placeholder  : getOutputPlaceholder(dataType)}
              $isFull={isFull}
              disabled={disabled}
              readOnly={readOnly || isFieldReadOnly(dataType)}
              isActiveDate={
                !isRelativeDate && ['date', 'dateTime'].includes(dataType ?? '')
              }
            />
          ) : (
            <TextAreaStyled
              value={formatValue(value)}
              $isSmall={isSmall}
              $isToken={nType !== 'constant'}
              $hasError={!_isNil(error)}
              $showExpandIcon={showExpandIcon}
              onChange={onInputChange}
              onBlur={handleOnBlur}
              onFocus={() => setIsFocused(true)}
              placeholder={placeholder ? placeholder  : getOutputPlaceholder(dataType)}
              $isFull={isFull}
              disabled={disabled}
              readOnly={readOnly || isFieldReadOnly(dataType)}
              rows={
                isFocused && nType === 'constant'
                  ? 2
                  : nType !== 'constant'
                  ? 1
                  : 1
              }
            />
          )}
        </ExecutedValueTooltip>

        {nType !== 'constant' && isValueToken && !fieldHasPillComponent && (
          <InputBrackets
            direction="right"
            className="right-brackets-container"
            $showExpandIcon={showExpandIcon}
            $hasRightIcon={!_isNil(rightIcon)}
          >
            <Typography>{'}}'}</Typography>
          </InputBrackets>
        )}

        {forceExpandListIcon && (
          <ExpandIconContainer
            onClick={() => {
              openTextInputModal({
                onChange: onInputChange,
                value,
                disabled,
                readOnly,
              });
            }}
          >
            <>⤢</>
          </ExpandIconContainer>
        )}
        {!_isNil(rightIcon) && !fieldHasPillComponent && (
          <RightIconContainer $ruleType={ruleType ?? ''} className="right-icon-container">
             {rightIcon(hidePopover)}
          </RightIconContainer>
        )}

        {!_isNil(rightIcon) && fieldHasPillComponent && (
          <div style={{ position: 'absolute', right: '0.4rem'}} onClick={(e) => e.stopPropagation()}>
            {rightIcon(hidePopover)}
          </div>
        )}
      </InputContainer>
    );
  }
);

RhsLauncher.displayName = 'RhsLauncher';
